import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ListingsAPI } from '@certi/shared/api/listings/ListingsAPI'

interface ListingResponse {
  id: string
  ownerId: string
  title: string
  description: string
  price: string
  category: string
  condition: string
  sportType: string
  mediaUrls: string[]
  status: string
  slug: string | null
  nfcUid: string | null
  createdAt: string
  updatedAt: string
  playerName: string | null
  teamOrClub: string | null
  leagueOrEvent: string | null
  dateOrSeason: string | null
  size: string | null
  tags: string[]
}

interface ListingsQuery {
  page?: number
  size?: number
  sort?: ListingSort
  filters?: any
  search?: string
}

interface ListingSort {
  field: 'createdAt' | 'updatedAt' | 'price' | 'title'
  direction: 'asc' | 'desc'
}

interface FilterChip {
  id: string
  label: string
}

const SEARCH_DEBOUNCE_MS = 300
import SearchBar from '@/components/marketplace/SearchBar'
import SortDropdown from '@/components/marketplace/SortDropdown'
import ListingCard from '@/components/marketplace/ListingCard'

export default function MarketplacePage() {
  const navigate = useNavigate()
  const [listings, setListings] = useState<ListingResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [favorites, setFavorites] = useState<Set<string>>(new Set())
  
  // Search and filtering state (mobile-inspired)
  const [searchText, setSearchText] = useState('')
  const [debouncedSearchText, setDebouncedSearchText] = useState('')
  const [selectedChip, setSelectedChip] = useState<string>('all')
  const [sort, setSort] = useState<ListingSort | undefined>({ field: 'createdAt', direction: 'desc' })
  const [currentPage, setCurrentPage] = useState(0)
  const [_totalPages, setTotalPages] = useState(0)
  const [totalElements, setTotalElements] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  // Filter chips data (mobile-inspired)
  const filterChips: FilterChip[] = [
    { id: 'all', label: 'All' },
    { id: 'jerseys', label: 'Jerseys' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'footwear', label: 'Footwear' },
    { id: 'accessories', label: 'Accessories' },
    { id: 'signed', label: 'Signed' },
    { id: 'ending', label: 'Ending Soon' }
  ]

  // Debounce search input (mobile feature)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchText(searchText)
    }, SEARCH_DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [searchText])

  // Convert chip selection to category filter
  const selectedCategory = useMemo(() => {
    switch (selectedChip) {
      case 'jerseys': return 'CLOTHING'
      case 'equipment': return 'EQUIPMENT' 
      case 'footwear': return 'FOOTWEAR'
      case 'accessories': return 'ACCESSORIES'
      default: return undefined
    }
  }, [selectedChip])

  const fetchListings = async (reset = false) => {
    try {
      setLoading(true)
      setError(null)

      const query: ListingsQuery = {
        page: reset ? 0 : currentPage,
        size: 20,
        search: debouncedSearchText || undefined,
        sort,
        filters: {
          category: selectedCategory,
          status: 'LISTED' // Only show listed items
        }
      }

      const response = await ListingsAPI.fetchListings(query)
      
      if (reset) {
        setListings(response.listings)
        setCurrentPage(0)
      } else {
        setListings(prev => [...prev, ...response.listings])
      }
      
      setTotalPages(response.totalPages)
      setTotalElements(response.totalElements)
      setHasMore(!response.last)
      
    } catch (err) {
      console.error('Failed to fetch listings:', err)
      setError(err instanceof Error ? err.message : 'Failed to load listings')
    } finally {
      setLoading(false)
    }
  }

  // Initial load and reload on filter changes (mobile-inspired)
  useEffect(() => {
    setCurrentPage(0)
    fetchListings(true)
  }, [debouncedSearchText, selectedCategory, sort])

  // Load more when page changes
  useEffect(() => {
    if (currentPage > 0) {
      fetchListings(false)
    }
  }, [currentPage])

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      setCurrentPage(prev => prev + 1)
    }
  }

  const handleListingClick = (listing: ListingResponse) => {
    if (listing.slug) {
      navigate(`/marketplace/${listing.slug}`)
    } else {
      navigate(`/marketplace/listing/${listing.id}`)
    }
  }

  const handleToggleFavorite = (listingId: string) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev)
      if (newFavorites.has(listingId)) {
        newFavorites.delete(listingId)
      } else {
        newFavorites.add(listingId)
      }
      return newFavorites
    })
    // TODO: Implement favorites API integration
  }

  const handleOpenFilters = () => {
    // TODO: Implement advanced filters modal
    console.log('Open advanced filters')
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={() => fetchListings(true)}
            className="btn btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      {/* Header */}
      <div className="py-6 border-b border-gray-200 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-brand-primary">certi</h1>
            <p className="text-gray-600">Authenticated Sports Memorabilia</p>
          </div>
          <button className="p-2 text-gray-400 hover:text-gray-600">
            <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar
        value={searchText}
        onChangeText={setSearchText}
        onOpenFilters={handleOpenFilters}
      />

      {/* Filter Chips - Mobile Style */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {filterChips.map((chip) => {
          const isSelected = selectedChip === chip.id
          return (
            <button
              key={chip.id}
              onClick={() => setSelectedChip(chip.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                isSelected
                  ? 'bg-brand-primary text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {chip.label}
            </button>
          )
        })}
      </div>

      {/* Divider - Mobile Style */}
      <div className="border-t border-gray-200 mb-6"></div>

      {/* Results Toolbar - Mobile Style */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-base font-medium text-gray-900">
          {totalElements > 0 ? (
            `${totalElements} items found`
          ) : (
            'No items found'
          )}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Sort by:</span>
          <SortDropdown sort={sort} onSortChange={setSort} />
        </div>
      </div>

      {/* Section Header - Mobile Style */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Featured Items</h2>
        <button 
          onClick={() => console.log('View all listings')}
          className="text-sm font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
        >
          View All
        </button>
      </div>

      {/* Listings Grid - Desktop Optimized */}
      {listings.length > 0 ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
            {listings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                isFavorite={favorites.has(listing.id)}
                onPress={() => handleListingClick(listing)}
                onToggleFavorite={handleToggleFavorite}
              />
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div className="text-center mb-8">
              <button
                onClick={handleLoadMore}
                disabled={loading}
                className="btn btn-secondary"
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      ) : !loading ? (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No listings found</h3>
          <p className="text-gray-500">Try adjusting your search or filters</p>
        </div>
      ) : null}

      {/* Loading State */}
      {loading && listings.length === 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse">
              <div className="flex gap-4">
                <div className="w-22 h-22 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-3 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-4"></div>
                  <div className="h-6 bg-gray-200 rounded w-20"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}